import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AnimateButton from "ui-component/extended/AnimateButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useBoundStore } from "useStore";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useCampaignMutation from "../Queries/CampaignMutation";
import { CmPublishSummary } from "./CmPublishSummary";
import { CmPublishTable } from "./CmPublishTable";
import { CmConfirmExitDialog } from "./CmConfirmExitDialog";
import { CmHighIncreaseWarning } from "./CmHighIncreaseWarning";
import { useCmPublishRows } from "./hooks/useCmPublishRows";
import { useCmRowSelection } from "./hooks/useCmRowSelection";
import { useCmPublishSubmit } from "./hooks/useCmPublishSubmit";

export default function PublishButton() {
  const cmEditedRows = useBoundStore((state) => state.cmEditedRows);
  const clearCmEditedRows = useBoundStore((state) => state.clearCmEditedRows);
  const setCampaignManagerSelectionModel = useBoundStore(
    (state) => state.setCampaignManagerSelectionModel,
  );
  const cmPublishOpen = useBoundStore((state) => state.cmPublishOpen);
  const setCmPublishOpen = useBoundStore((state) => state.setCmPublishOpen);

  const open = cmPublishOpen;
  const setOpen = setCmPublishOpen;
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendState, setSendState] = useState("pending");
  const [feedbackByRow, setFeedbackByRow] = useState({});

  const mutation = useCampaignMutation();
  const queryClient = useQueryClient();
  const { allModifiedRowKeys, numberOfCampaignsWithHighIncrease, threshold } =
    useCmPublishRows(cmEditedRows);

  const {
    selectedRows,
    hasSelectedRows,
    allSelected,
    someSelected,
    toggleRow,
    toggleAll,
    resetSelection,
  } = useCmRowSelection(open, allModifiedRowKeys, cmEditedRows, threshold);

  const handleSubmit = useCmPublishSubmit({
    mutation,
    allModifiedRowKeys,
    selectedRows,
    cmEditedRows,
    setFeedbackByRow,
    setLoading,
    setSendState,
  });

  const isDisabled = allModifiedRowKeys.length === 0;
  const totalCount = allModifiedRowKeys.length;

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setOpenModal(false);
    resetSelection();

    if (sendState === "success") {
      setCampaignManagerSelectionModel([]);
      clearCmEditedRows();
      setSendState("pending");
      setFeedbackByRow({});
      queryClient.invalidateQueries({ queryKey: ["campaignData"] });
    }
  };

  return (
    <Stack direction="row" gap={2} alignItems="stretch">
      <AnimateButton style={{ width: "100%", display: "flex" }}>
        <Button
          fullWidth
          style={{
            height: "100%",
            backgroundColor: isDisabled ? "#0000001F" : "#002878",
            "&:hover": { backgroundColor: "#002878" },
          }}
          endIcon={<UploadFileIcon />}
          onClick={handleOpen}
          variant="contained"
          disabled={isDisabled}
        >
          Publicar
        </Button>
      </AnimateButton>

      <Dialog
        open={open}
        onClose={loading ? undefined : handleClose}
        maxWidth="lg"
        fullWidth
      >
        <Stack direction="column" spacing={2} padding={2}>
          <DialogTitle sx={{ backgroundColor: "#E5E7EB" }}>
            {sendState === "success"
              ? "Modificaciones enviadas"
              : "Confirma las modificaciones realizadas"}
          </DialogTitle>
        </Stack>

        <DialogContent>
          <CmPublishSummary
            allModifiedRowKeys={allModifiedRowKeys}
            selectedRows={selectedRows}
          />

          <Divider sx={{ marginY: 2 }} />

          <CmPublishTable
            allModifiedRowKeys={allModifiedRowKeys}
            selectedRows={selectedRows}
            handleRowSelectionChange={toggleRow}
            allSelected={allSelected}
            someSelected={someSelected}
            handleSelectAll={toggleAll}
            loading={loading}
            sendState={sendState}
            feedbackByRow={feedbackByRow}
          />

          <CmHighIncreaseWarning
            numberOfCampaignsWithHighIncrease={
              numberOfCampaignsWithHighIncrease
            }
            threshold={threshold}
          />
        </DialogContent>

        <DialogActions>
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            alignItems="center"
            paddingX={2}
          >
            {loading && sendState !== "success" && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ backgroundColor: "#E5F6FD", padding: 1, borderRadius: 1 }}
              >
                <ErrorOutlineOutlinedIcon sx={{ color: "#0288D1" }} />
                <Typography sx={{ color: "#014361", fontSize: "14px" }}>
                  Por favor no cerrar el modal ni refrescar la página.
                </Typography>
              </Stack>
            )}

            {sendState === "success" && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ backgroundColor: "#E5F6FD", padding: 1, borderRadius: 1 }}
              >
                <ErrorOutlineOutlinedIcon sx={{ color: "#0288D1" }} />
                <Stack direction="column">
                  <Typography
                    sx={{ color: "#014361", fontSize: "14px", fontWeight: 400 }}
                  >
                    Los cambios fueron enviados
                  </Typography>
                  <Typography sx={{ color: "#014361", fontSize: "12px" }}>
                    Puedes verificar el resultado en la tabla.
                  </Typography>
                </Stack>
              </Stack>
            )}

            <Button
              onClick={
                sendState === "success" ? handleClose : () => setOpenModal(true)
              }
              disabled={loading}
              variant="outline"
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 500,
                color: "#354887",
                height: "32px",
                textTransform: "uppercase",
                border: "1px solid #354887",
                "&:hover": {
                  backgroundColor: "#F5F5F5",
                  borderColor: "#2A3B6F",
                  color: "#2A3B6F",
                },
              }}
            >
              {sendState === "success" ? "Cerrar" : "Cancelar"}
            </Button>

            <CmConfirmExitDialog
              openModal={openModal}
              handleClose={handleClose}
              handleClickClose={() => setOpenModal(false)}
              totalCount={totalCount}
            />

            <Button
              onClick={handleSubmit}
              disabled={!hasSelectedRows || loading || sendState === "success"}
              variant="contained"
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 500,
                color: "#ffffff",
                backgroundColor: hasSelectedRows ? "#354887" : "#0000001F",
                height: "32px",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: hasSelectedRows ? "#2c3b6f" : "#0000001F",
                },
              }}
            >
              {loading && sendState === "pending" ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography
                    sx={{ color: "rgba(0,0,0,0.38)", fontSize: "14px" }}
                  >
                    Publicando...
                  </Typography>
                  <CircularProgress sx={{ color: "#8B8B8B" }} size={18} />
                </Box>
              ) : sendState === "success" ? (
                "Enviado"
              ) : sendState === "error" ? (
                "Error al enviar"
              ) : (
                "Enviar cambios"
              )}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
